import { useRef, useState } from 'react';
import { Button, makeStyles, shorthands, tokens } from '@fluentui/react-components';
import {
  Alert,
  AlertDescription,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  RadioGroup,
} from '@/fluent-adapter';
import { submitEEvidenceOrder } from '../api/eEvidenceApi';
import type {
  EEvidenceRequestType,
  EEvidenceSubmissionResult,
} from '../api/contracts';
import { isEEvidenceApiFailure } from '../api/errors';

const useStyles = makeStyles({
  page: {
    width: '100%',
    maxWidth: '760px',
    marginLeft: 'auto',
    marginRight: 'auto',
    ...shorthands.padding(tokens.spacingVerticalXXL, tokens.spacingHorizontalL),
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalL,
  },
  title: {
    margin: 0,
    fontSize: tokens.fontSizeHero700,
    fontWeight: tokens.fontWeightSemibold,
  },
  muted: { margin: 0, color: tokens.colorNeutralForeground3 },
  field: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalS,
  },
  label: { fontWeight: tokens.fontWeightSemibold },
  actions: {
    display: 'flex',
    columnGap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  details: {
    display: 'grid',
    gridTemplateColumns: 'max-content 1fr',
    gap: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
    margin: 0,
  },
  term: { fontWeight: tokens.fontWeightSemibold },
  value: { margin: 0, overflowWrap: 'anywhere' },
});

interface EEvidenceSubmissionProps {
  onExit: () => void;
}

function errorMessage(error: unknown): string {
  return isEEvidenceApiFailure(error)
    ? error.message
    : 'Something went wrong. Please try again shortly.';
}

function isPdf(file: File): boolean {
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
}

export function EEvidenceSubmission({ onExit }: EEvidenceSubmissionProps) {
  const styles = useStyles();
  const [requestType, setRequestType] = useState<EEvidenceRequestType>('epoc');
  const [isEmergency, setIsEmergency] = useState<boolean>();
  const [file, setFile] = useState<File>();
  const [result, setResult] = useState<EEvidenceSubmissionResult>();
  const [error, setError] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const idempotencyKey = useRef<string>();
  const isBusy = isSubmitting;
  const needsEmergencyAnswer = requestType === 'epoc' && isEmergency === undefined;

  const resetAttempt = () => {
    idempotencyKey.current = undefined;
    setResult(undefined);
    setError(undefined);
  };

  const handleSubmit = async () => {
    if (file === undefined || !isPdf(file)) {
      setError('Select exactly one PDF before submitting.');
      return;
    }

    const key = idempotencyKey.current ?? crypto.randomUUID();
    idempotencyKey.current = key;
    setError(undefined);
    setIsSubmitting(true);

    try {
      setResult(
        await submitEEvidenceOrder(
          {
            requestType,
            isEmergency: requestType === 'epoc' ? isEmergency : undefined,
            requestPdf: file,
          },
          key,
        ),
      );
    } catch (submitError) {
      // A conflict ends the submission, so a retry must start over under a new key.
      if (isEEvidenceApiFailure(submitError) && submitError.code === 'Conflict') {
        idempotencyKey.current = undefined;
      }
      setError(errorMessage(submitError));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.page}>
      <div>
        <Button appearance="subtle" disabled={isBusy} onClick={onExit}>
          Back
        </Button>
      </div>

      <div>
        <h1 className={styles.title}>European E-Evidence order</h1>
        <p className={styles.muted}>
          Submit one completed PDF for a Form 1 or Form 2 order.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Order details</CardTitle>
          <CardDescription>Forms 5 and 6 are not part of this flow.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className={styles.field}>
            <span className={styles.label}>Order type</span>
            <RadioGroup
              aria-label="Order type"
              disabled={isBusy}
              value={requestType}
              onValueChange={(value) => {
                setRequestType(value as EEvidenceRequestType);
                setIsEmergency(undefined);
                resetAttempt();
              }}
              items={[
                { value: 'epoc', label: 'Form 1: Production order' },
                { value: 'epocPr', label: 'Form 2: Preservation order' },
              ]}
            />
          </div>

          {requestType === 'epoc' && (
            <div className={styles.field}>
              <span className={styles.label}>Is this an emergency?</span>
              <RadioGroup
                aria-label="Is this an emergency?"
                disabled={isBusy}
                value={isEmergency === undefined ? '' : String(isEmergency)}
                onValueChange={(value) => {
                  setIsEmergency(value === 'true');
                  resetAttempt();
                }}
                items={[
                  { value: 'true', label: 'Yes' },
                  { value: 'false', label: 'No' },
                ]}
              />
            </div>
          )}

          <div className={styles.field}>
            <label className={styles.label} htmlFor="e-evidence-pdf">
              Completed order PDF
            </label>
            <input
              id="e-evidence-pdf"
              type="file"
              accept="application/pdf,.pdf"
              disabled={isBusy}
              onChange={(event) => {
                setFile(event.target.files?.item(0) ?? undefined);
                resetAttempt();
              }}
            />
          </div>

          {error !== undefined && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className={styles.actions}>
            <Button
              appearance="primary"
              disabled={file === undefined || needsEmergencyAnswer || isBusy}
              onClick={handleSubmit}
            >
              {isSubmitting ? 'Submitting…' : 'Submit order'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {result !== undefined && (
        <Card>
          <CardHeader>
            <CardTitle as="h2">Submission acknowledgement</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className={styles.details}>
              <dt className={styles.term}>Submission state</dt>
              <dd className={styles.value}>{result.submissionState}</dd>
              <dt className={styles.term}>Case ID</dt>
              <dd className={styles.value}>{result.caseId}</dd>
            </dl>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
